import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { TaskAltOutlined as ResolveIcon } from "@mui/icons-material";
import { useMemo, useState } from "react";

import AppDialog from "components/common/AppDialog";
import {
  useGetJobManualResolutionPreviewQuery,
  useResolveJobEmployeeFilesMutation,
} from "features/adminUsers/adminUsers";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { formatApiError } from "utils/formatApiError";
import {
  employeeLabel,
  formatResolvedAt,
  LogRecordDto,
  ManualResolutionFile,
  ManualResolutionKind,
  ManualResolutionSaveResponse,
  manualResolutionKey,
} from "./logRecords";
import { describeOutcome, plural, resolutionWording } from "./manualResolution";
import { ROSTER_ROW_HINT, rosterRowLabel } from "./rosterFile";

export interface ManualResolveDialogProps {
  open: boolean;
  /** The upload or download job; in task scope this is the log-records payload's job_id. */
  jobId: number | null;
  /** Upload: file by file. Download: the employee as a whole. */
  kind: ManualResolutionKind;
  /** The status row the operator clicked: an error, or a manually resolved success. */
  record: LogRecordDto | null;
  canEdit: boolean;
  /** Client View / manager role / a backend blocker; null when the operator may act. */
  readOnlyReason: string | null;
  onClose: () => void;
  /** After a successful save; the viewer reloads. The dialog stays open with the outcome. */
  onSaved: () => void;
}

const ERROR_TEXTS: Record<string, string> = {
  tasks_in_flight:
    "Stop the job's tasks first - files cannot be marked while a task is queued or running.",
  unsupported_job_kind: "Only upload and download jobs can have employees marked as resolved.",
  unknown_filename:
    "A selected file is no longer on the list - the list was reloaded, check it and save again.",
  nothing_to_do: "Nothing changed - tick or untick a file first.",
  files_remaining: "Files of this employee are still rejected - tick them instead.",
  archive_recently_written:
    "The bot wrote this log archive less than two minutes ago - wait a moment and try again.",
  archive_changed:
    "The log archive changed while it was being updated - the list was reloaded, try again.",
  archive_corrupt: "The stored log archive could not be read as a zip.",
  employee_not_found:
    "The employee was not found in the job's stored logs - they may have been cleared.",
  storage_unavailable:
    "The log archive could not be read or written right now. Try again in a minute.",
  storage_error:
    "The log archive could not be read or written right now. Try again in a minute.",
  no_archive: "No stored log archive holds this employee - nothing to write the resolution into.",
};

function errorPayload(error: unknown): Record<string, unknown> | undefined {
  const data = (error as { data?: unknown } | undefined)?.data;
  return data && typeof data === "object" ? (data as Record<string, unknown>) : undefined;
}

function errorCode(error: unknown): string {
  const code = errorPayload(error)?.code;
  return typeof code === "string" ? code : "";
}

function describeSaveError(error: unknown): string {
  const payload = errorPayload(error);
  const code = errorCode(error);
  if (code && ERROR_TEXTS[code]) return ERROR_TEXTS[code];
  if (typeof payload?.message === "string" && payload.message) return payload.message;
  return formatApiError(payload, "The files could not be marked as resolved.");
}

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 || value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

function StatusChip({ file }: { file: ManualResolutionFile }) {
  if (file.status === "resolved_manually") {
    return (
      <Stack spacing={0.25} alignItems="flex-start">
        <Chip size="small" variant="outlined" color="success" label="resolved manually" />
        <Typography variant="caption" color="text.secondary">
          by {file.resolved_by || "an operator"}
          {file.resolved_at ? ` at ${formatResolvedAt(file.resolved_at)}` : ""}
        </Typography>
      </Stack>
    );
  }
  if (file.status === "not_attempted") {
    return <Chip size="small" variant="outlined" label="not attempted" />;
  }
  return (
    <Chip
      size="small"
      variant="outlined"
      color={file.state === "warning" ? "warning" : "error"}
      label="rejected"
    />
  );
}

/**
 * "Manually resolved" for one employee of an upload job: every file the logs
 * (or, failing those, the Data Storage folder) hold for them, each with a
 * checkbox meaning "uploaded by hand". Files already marked come pre-ticked;
 * un-ticking one undoes its resolution. One Save sends the difference. The
 * dialog stays open afterwards with the outcome, and the list re-mirrors the
 * server. A download job lists no files: its one row is the employee.
 */
export default function ManualResolveDialog({
  open,
  jobId,
  kind,
  record,
  canEdit,
  readOnlyReason,
  onClose,
  onSaved,
}: ManualResolveDialogProps) {
  const dispatch = useAppDispatch();
  const wording = resolutionWording(kind);
  const key = record ? manualResolutionKey(record) : null;
  const preview = useGetJobManualResolutionPreviewQuery(
    {
      jobId: jobId ?? 0,
      employee_id: key?.employee_id ?? null,
      inner_number: key?.inner_number ?? null,
      s3_name: key?.s3_name ?? null,
    },
    { skip: !open || jobId == null || key == null, refetchOnMountOrArgChange: true },
  );
  // currentData, not data: `data` keeps the previous employee's answer while
  // the next one loads.
  const data = preview.currentData;
  const [save, { isLoading: saving }] = useResolveJobEmployeeFilesMutation();

  // null = untouched, mirrors the server (resolved files ticked, the rest not).
  const [checked, setChecked] = useState<Record<string, boolean> | null>(null);
  const [employeeChecked, setEmployeeChecked] = useState<boolean | null>(null);
  const [outcome, setOutcome] = useState<ManualResolutionSaveResponse | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const files = data?.files ?? [];
  const serverChecked = useMemo(
    () =>
      Object.fromEntries(
        files.map((file) => [file.filename, file.status === "resolved_manually"]),
      ) as Record<string, boolean>,
    [files],
  );
  const effective = checked ?? serverChecked;
  const employeeServer = Boolean(data?.employee.manual && data.employee.state === "success");
  const employeeEffective = employeeChecked ?? employeeServer;
  const noCandidates = data != null && files.length === 0;

  const resolve = files
    .filter((file) => file.status !== "resolved_manually" && effective[file.filename])
    .map((file) => file.filename);
  const undo = files
    .filter((file) => file.status === "resolved_manually" && !effective[file.filename])
    .map((file) => file.filename);
  const resolveEmployee = noCandidates && employeeEffective && !employeeServer;
  const undoEmployee = noCandidates && !employeeEffective && employeeServer;
  const dirty = resolve.length + undo.length > 0 || resolveEmployee || undoEmployee;

  const blocked =
    readOnlyReason ??
    (data && !data.can_resolve
      ? data.blockers[0] ?? "This employee cannot be resolved right now."
      : null);
  const controlsDisabled = !canEdit || blocked != null || saving || !data;
  const saveDisabledReason =
    blocked ?? (!data ? "Loading..." : !dirty ? "Tick or untick a file first." : "");
  const canSave = canEdit && !saving && saveDisabledReason === "";

  // The row the clicked chip shows: the preview reads the logs without the
  // roster, so its own number is the row the run walked.
  const row = record?.inner_number ?? data?.employee.inner_number ?? null;
  const label = data
    ? employeeLabel({ ...data.employee, inner_number: row })
    : record
      ? employeeLabel(record)
      : "";
  const loading = open && !data && (preview.isLoading || preview.isFetching);
  const previewError = !data && preview.error ? preview.error : undefined;

  const allChecked = files.length > 0 && files.every((file) => effective[file.filename]);
  const someChecked = files.some((file) => effective[file.filename]);

  const toggle = (filename: string, value: boolean) => {
    setOutcome(null);
    setChecked({ ...effective, [filename]: value });
  };
  const toggleAll = (value: boolean) => {
    setOutcome(null);
    setChecked(Object.fromEntries(files.map((file) => [file.filename, value])));
  };

  const saveLabel = saving
    ? "Saving..."
    : resolve.length && undo.length
      ? `Save: mark ${resolve.length} as resolved, undo ${undo.length}`
      : resolve.length
        ? `Mark ${plural(resolve.length, "file")} as resolved`
        : undo.length
          ? `Undo ${plural(undo.length, "file")}`
          : resolveEmployee
            ? "Mark employee as resolved"
            : undoEmployee
              ? "Undo employee resolution"
              : "Save";

  const handleSave = async () => {
    if (!record || jobId == null || !key) return;
    setSaveError(null);
    try {
      const response = await save({
        jobId,
        ...key,
        resolve,
        undo,
        resolve_employee: resolveEmployee || undefined,
        undo_employee: undoEmployee || undefined,
      }).unwrap();
      setOutcome(response);
      setChecked(null);
      setEmployeeChecked(null);
      const reopened = response.undone_files.length > 0 || response.status_record === "remove";
      dispatch(
        pushNotification({
          severity: response.employee_resolved ? "success" : reopened ? "warning" : "info",
          message: describeOutcome(response, label, kind),
        }),
      );
      onSaved();
      void preview.refetch();
    } catch (error) {
      setSaveError(describeSaveError(error));
      const code = errorCode(error);
      if (code === "unknown_filename" || code === "archive_changed") {
        setChecked(null);
        setEmployeeChecked(null);
        void preview.refetch();
      }
    }
  };

  const employee = data?.employee;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      showCloseIcon
      title={
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 800 }}>
            {wording.title}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label}
            {employee?.s3_name ? ` - ${employee.s3_name}` : ""}
          </Typography>
        </Box>
      }
      actions={
        <>
          <Button onClick={onClose}>Close</Button>
          <Tooltip title={canSave ? "" : saveDisabledReason}>
            <span>
              <Button
                variant="contained"
                startIcon={
                  saving ? <CircularProgress size={14} color="inherit" /> : <ResolveIcon />
                }
                disabled={!canSave}
                onClick={() => void handleSave()}
              >
                {saveLabel}
              </Button>
            </span>
          </Tooltip>
        </>
      }
    >
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        {loading && (
          <>
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={160} />
          </>
        )}
        {previewError && (
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={() => void preview.refetch()}>
                Retry
              </Button>
            }
          >
            The files of this employee could not be read:{" "}
            {formatApiError(errorPayload(previewError), "the server returned an error.")}
          </Alert>
        )}
        {data && employee && (
          <>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                <Typography variant="subtitle2" sx={{ mr: 0.5 }}>
                  {employee.employee_name || employee.employee_id || "(no employee)"}
                </Typography>
                {row != null && (
                  <Tooltip title={ROSTER_ROW_HINT}>
                    <Chip size="small" variant="outlined" label={rosterRowLabel(row)} />
                  </Tooltip>
                )}
                {employee.employee_id && (
                  <Chip size="small" variant="outlined" label={employee.employee_id} />
                )}
                <Chip
                  size="small"
                  variant="outlined"
                  color={employee.state === "success" ? "success" : "error"}
                  label={employee.state === "success" ? "SUCCESS" : employee.state.toUpperCase()}
                />
                {employee.manual && (
                  <Chip size="small" color="success" label="RESOLVED (manually)" />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                {[
                  employee.total != null ? `total ${employee.total}` : null,
                  employee.downloaded != null ? `downloaded ${employee.downloaded}` : null,
                  employee.uploaded_count != null
                    ? `uploaded${employee.uploaded_to ? ` to ${employee.uploaded_to}` : ""} ${employee.uploaded_count}`
                    : null,
                  employee.attempts > 1 ? `${employee.attempts} attempts` : null,
                ]
                  .filter(Boolean)
                  .join(" - ")}
              </Typography>
            </Paper>

            {!data.can_resolve && <Alert severity="warning">{data.blockers.join(" ")}</Alert>}
            {data.can_resolve && readOnlyReason && <Alert severity="info">{readOnlyReason}</Alert>}
            {data.files_source === "folder" && (
              <Typography variant="caption" color="text.secondary">
                The logs name no rejected file for this employee. The files below are what the
                employee&apos;s Data Storage folder holds - tick the ones you uploaded by hand.
              </Typography>
            )}
            {outcome && (
              <Alert
                severity={
                  outcome.employee_resolved
                    ? "success"
                    : outcome.undone_files.length || outcome.status_record === "remove"
                      ? "warning"
                      : "info"
                }
              >
                {describeOutcome(outcome, label, kind)}
              </Alert>
            )}
            {(undo.length > 0 || undoEmployee) && (
              <Alert severity="warning">{wording.undoWarning}</Alert>
            )}
            {saveError && <Alert severity="error">{saveError}</Alert>}

            <Box sx={{ maxHeight: 360, overflow: "auto" }}>
              <Table size="small" stickyHeader>
                {kind === "upload" && (
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={allChecked}
                          indeterminate={someChecked && !allChecked}
                          disabled={controlsDisabled || files.length === 0}
                          onChange={(event) => toggleAll(event.target.checked)}
                          inputProps={{ "aria-label": "Select all files" }}
                        />
                      </TableCell>
                      <TableCell>File</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {files.map((file) => (
                    <TableRow
                      key={file.filename}
                      hover
                      onClick={() => {
                        if (!controlsDisabled) toggle(file.filename, !effective[file.filename]);
                      }}
                      sx={{ cursor: controlsDisabled ? "default" : "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={Boolean(effective[file.filename])}
                          disabled={controlsDisabled}
                          onChange={(event) => toggle(file.filename, event.target.checked)}
                          onClick={(event) => event.stopPropagation()}
                          inputProps={{ "aria-label": `Uploaded manually: ${file.filename}` }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12, overflowWrap: "anywhere" }}>
                        {file.filename}
                        {file.source === "folder" && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            from folder{file.size != null ? ` - ${formatBytes(file.size)}` : ""}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, overflowWrap: "anywhere" }}>
                        {file.reason ?? "-"}
                      </TableCell>
                      <TableCell>
                        <StatusChip file={file} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {noCandidates && (
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={employeeEffective}
                          disabled={controlsDisabled}
                          onChange={(event) => {
                            setOutcome(null);
                            setEmployeeChecked(event.target.checked);
                          }}
                          inputProps={{ "aria-label": "Employee marked as resolved" }}
                        />
                      </TableCell>
                      <TableCell colSpan={3}>
                        <Typography variant="body2">{wording.employeeRowTitle}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {wording.employeeRowHint}
                        </Typography>
                        {employeeServer && employee.resolved_by && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            by {employee.resolved_by}
                            {employee.resolved_at
                              ? ` at ${formatResolvedAt(employee.resolved_at)}`
                              : ""}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </Stack>
    </AppDialog>
  );
}
